import {Box, Hidden, Typography} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import {Autocomplete} from 'mui-rff';
import React, {useRef, useState} from "react";
import {useForm, useFormState} from "react-final-form";
import {TwoWaySwitch} from "../Input/TwoWaySwitch";
import {MyComponent, MyProject, useProjects} from "./MyProjectsProvider";
import TaskSelect from "./TaskSelect";

type Props = {
    setNoteFocus : () => void;
};

const ProjectComponentTaskSelect : React.FC<Props> = ({setNoteFocus} : Props) => {
    const [components, setComponents] = React.useState<MyComponent[]>([]);
    const componentRef = useRef<HTMLDivElement | null>(null);
    const taskRef = useRef<HTMLDivElement | null>(null);
    const {myProjects, allProjects, cachedProjects, getProject} = useProjects();
    const form = useForm();
    const formState = useFormState();
    const [useAllProjects, setUseAllProjects] = useState(false);

    const options : Array<Omit<MyProject, 'isPmRole' | 'roles'| 'components'| 'pmFixedBilling'>> = useAllProjects ? allProjects : myProjects;

    React.useEffect(() => {
        (async () => {
            if (formState.values.project) {
                const projectId = formState.values.project.id;
                let myProject = myProjects.find((p) => p.id === projectId);
                if (!myProject) {
                    myProject = cachedProjects.find((p) => p.id === projectId);
                    if (!myProject) {
                        myProject = await getProject(projectId);
                    }
                }
                setComponents(myProject?.components ? myProject.components : []);
            } else {
                setComponents(formState.values.component ? [formState.values.component] : []);
            }
        })();
    }, [cachedProjects, formState.values.component, formState.values.project, getProject, myProjects])

    React.useEffect(() => {
        let myProject = null;
        if (formState.values.task?.projectId && formState.values.project?.id !== formState.values.task.projectId) {
            myProject = myProjects.find((p) => p.id === formState.values.task?.projectId);
            form.change('project', myProject ?? null);
        }
        if (formState.values.task?.componentId && formState.values.component?.id !== formState.values.task.componentId) {
            //the find above might not trigger if you pick a project but not a component and then pick a task.
            myProject = myProject ?? myProjects.find((p) => p.id === formState.values.task?.projectId);
            const component = myProject?.components?.find((c) => c.id === formState.values.task.componentId)
            form.change('component', component);
        }
    }, [form, formState.values.component, formState.values.project, formState.values.task, myProjects])

    return <>
        <Grid item>
            <Grid container direction="row" alignItems="center">
                <Grid item xs={12} sm={9} md={10}>
                    <Autocomplete
                        id="projectSelect"
                        name="project"
                        label="Project"
                        openOnFocus
                        options={options}
                        textFieldProps={{variant: 'outlined'}}
                        getOptionLabel={(option) => !option ? '' : option.name}
                        renderOption={option => (
                            <div style={{width: '100%'}}>
                                <div>{option.name}</div>
                            </div>
                        )}
                        getOptionSelected={(option, value) => option.id === value.id}
                        onChange={(_event, value) => {
                            form.change('component', null);
                            form.change('task', null);

                            let isPMTimeEntry = 0;
                            if (value && !Array.isArray(value) && typeof value !== 'string') {
                                const myProject = myProjects.find(p => p.id === value.id);
                                if (myProject?.roles?.length) {
                                    isPMTimeEntry = myProject.isPmRole && myProject.pmFixedBilling ? 1 : 0;
                                }
                            }
                            form.change('isPMTimeEntry', isPMTimeEntry);

                            if (value && componentRef.current && componentRef.current.focus) {
                                componentRef.current.focus();
                            }
                        }}
                        noOptionsText="No Projects"
                    />
                </Grid>
                <Hidden xsDown>
                    <Grid item sm={3} md={2}>
                        <Box marginTop="5px" marginLeft="1em">
                            <Typography>
                                My
                                <TwoWaySwitch
                                    checked={useAllProjects}
                                    onChange={event => setUseAllProjects(event.target.checked)}
                                />
                                All
                            </Typography>
                        </Box>
                    </Grid>
                </Hidden>
            </Grid>
        </Grid>
        <Grid item>
            <Autocomplete
                id="componentSelect"
                name="component"
                label="Component"
                openOnFocus
                options={components}
                textFieldProps={{
                    variant: 'outlined',
                    inputRef: componentRef
                }}
                getOptionLabel={(option) => !option ? '' : option.name}
                renderOption={option => (
                    <div style={{width: '100%'}}>
                        <div>{option.name}</div>
                    </div>
                )}
                getOptionSelected={(option, value) => option.id === value.id}
                onChange={(event, value) => {
                    form.change('task', null);
                    if (value && taskRef.current && taskRef.current.focus) {
                        taskRef.current.focus();
                    }
                }}
                noOptionsText={formState.values.project ? "No Components" : "Select a project first"}
            />
        </Grid>
        <Grid item>
            <TaskSelect
                label="Task"
                name="task"
                required={false}
                taskHierarchy={true}
                setNoteFocus={setNoteFocus}
                taskRef={taskRef}
            />
        </Grid>
    </>;
}

export default ProjectComponentTaskSelect;
